'use client';
/**
 * Paramétrage RH (lot 2) — l'écran où vivent TOUTES les règles du temps de
 * travail. Décision du 2026-07-28 : rien n'est codé en dur, tout est choisi ici
 * par le gestionnaire RH.
 *
 * Deux partis pris qui expliquent la forme de l'écran :
 *  · les champs arrivent VIDES, pas pré-remplis de valeurs plausibles. Le
 *    logiciel n'invente aucune durée légale tunisienne — un chiffre inventé qui
 *    ressemble à une valeur officielle est pire qu'un champ vide ;
 *  · tant qu'une règle manque, le module le DIT (soldes « — » et non « 0 »).
 *    Un zéro se lit comme un solde épuisé.
 */
import React from 'react';
import { enTeteCollant } from '@/shared/Tableau';
import { useGed } from '@/lib/store';
import { RhFeriesEcran } from '@/components/screens/RhFeries';
import type { RhMotif, RhParametres as TParametres } from '@/lib/types';
import { I, Eyebrow, card, inputStyle, Help, ConfirmButton, RefISO, useEscape, BarreActions } from '@/components/ui';
import { AccesRefuseRh, Modale, lbl } from '@/components/screens/RhAbsences';

import { Bouton } from '@/shared/Bouton';
const MODES_CONGES: Record<string, string> = {
  fixe: 'Nombre de jours fixe, le même pour tous',
  mensuel: 'Acquisition mensuelle (x jours par mois travaillé)',
  anciennete: 'Barème selon l’ancienneté',
};
const MODES_DECOMPTE: Record<string, string> = {
  ouvrables: 'Jours ouvrables — le samedi compte (lundi→samedi)',
  ouvres: 'Jours ouvrés — le samedi ne compte pas (lundi→vendredi)',
  planning: 'Selon le planning réel de la personne',
};

/**
 * Règles du temps de travail — monté comme onglet du CENTRE DE PARAMÉTRAGE
 * (décision du 28/07 : les réglages vivent tous au même endroit, derrière la
 * roue crantée). Ce n'est plus une vue de navigation.
 */
export function ReglesRh() {
  const { rhAcces, rhParametres, saveRhParametres } = useGed();
  const [f, setF] = React.useState<Partial<TParametres>>({});
  // Ces règles pilotent le décompte des congés et la paie : deux envois
  // concurrents, c'est le second qui gagne, sans que rien ne dise lequel.
  const [envoi, setEnvoi] = React.useState(false);
  // Resynchronisation pendant le rendu, pas dans un effet : le formulaire part
  // des paramètres en base et doit suivre s'ils sont rechargés ailleurs.
  const [vus, setVus] = React.useState(rhParametres);
  if (vus !== rhParametres) {
    setVus(rhParametres);
    if (rhParametres) setF(rhParametres);
  }

  if (!rhAcces?.acces) return <AccesRefuseRh />;
  const maj = (k: keyof TParametres, v: unknown) => setF((x) => ({ ...x, [k]: v }));

  const majMotif = (code: string, champ: keyof RhMotif, v: unknown) => {
    const l = (f.motifsAbsence || []).map((m) => (m.code === code ? { ...m, [champ]: v } : m));
    maj('motifsAbsence', l);
  };

  const droitNonRenseigne =
    (f.congesMode === 'fixe' && !f.congesJoursFixe)
    || (f.congesMode === 'mensuel' && !f.congesJoursParMois)
    || (f.congesMode === 'anciennete' && !(f.congesBaremeAnciennete || []).length);

  return (
    <section>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '0 0 4px', lineHeight: 1.55 }}>
        Les règles du temps de travail de votre laboratoire. Elles ne sont pas devinées : tant qu’une
        règle n’est pas renseignée, les soldes affichent « — » plutôt qu’un zéro qui ressemblerait à
        un solde épuisé. <RefISO code="6.2" />
      </p>

      {droitNonRenseigne && (
        <div style={{ ...card, padding: 20, background: 'var(--amber-50)', border: '1px solid var(--amber-500)', marginTop: 14 }}>
          <strong style={{ fontSize: 13, color: 'var(--amber-600)' }}>Droit à congé non renseigné</strong>
          <p style={{ fontSize: 12.5, margin: '4px 0 0' }}>
            Aucun solde ne peut être calculé tant que ce bloc est vide.
          </p>
        </div>
      )}

      {/* ---- Droit à congé ---- */}
      <Bloc titre="Droit à congé annuel">
        <label style={lbl}>Mode de calcul
          <select style={{ ...inputStyle, width: '100%' }} value={f.congesMode || 'fixe'} onChange={(e) => maj('congesMode', e.target.value)}>
            {Object.entries(MODES_CONGES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>

        {f.congesMode === 'mensuel' ? (
          <label style={{ ...lbl, marginTop: 10 }}>Jours acquis par mois travaillé
            <input type="number" step="0.5" min="0" placeholder="non renseigné" style={{ ...inputStyle, maxWidth: 180 }}
              value={f.congesJoursParMois || ''} onChange={(e) => maj('congesJoursParMois', Number(e.target.value))} />
          </label>
        ) : f.congesMode === 'anciennete' ? (
          <BaremeAnciennete
            bareme={f.congesBaremeAnciennete || []}
            onChange={(b) => maj('congesBaremeAnciennete', b)}
          />
        ) : (
          <label style={{ ...lbl, marginTop: 10 }}>Jours par an
            <input type="number" step="0.5" min="0" placeholder="non renseigné" style={{ ...inputStyle, maxWidth: 180 }}
              value={f.congesJoursFixe || ''} onChange={(e) => maj('congesJoursFixe', Number(e.target.value))} />
          </label>
        )}
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8 }}>
          Un contrat peut porter un droit différent : le cas particulier vit sur le contrat, jamais
          sur cette règle générale. La quotité d’un temps partiel est appliquée automatiquement.
        </p>
      </Bloc>

      {/* ---- Report ---- */}
      <Bloc titre="Report des congés non pris">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={lbl}>
            Plafond de report (jours)
            <Help aide="Laisser vide = report intégral, sans plafond. Le reliquat au-delà du plafond est signalé comme perdu, jamais escamoté en silence.">optionnel</Help>
            <input type="number" step="0.5" min="0" style={{ ...inputStyle, width: '100%' }}
              value={f.reportPlafond ?? ''} onChange={(e) => maj('reportPlafond', e.target.value === '' ? null : Number(e.target.value))} />
          </label>
          <label style={lbl}>
            À consommer avant le (MM-JJ)
            <input placeholder="03-31" style={{ ...inputStyle, width: '100%' }}
              value={f.reportDateLimite ?? ''} onChange={(e) => maj('reportDateLimite', e.target.value || null)} />
          </label>
        </div>
      </Bloc>

      {/* ---- Décompte ---- */}
      {/* ---- Demandes de congé ---- */}
      <Bloc titre="Demandes de congé">
        <label style={lbl}>Congés simultanés admis
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="number" min="1" max="30" style={{ ...inputStyle, width: 110 }}
              value={f.congesSimultanesMax ?? 2}
              onChange={(e) => maj('congesSimultanesMax', Number(e.target.value))} />
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>personne(s) en même temps</span>
          </div>
        </label>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.55 }}>
          PRO.G1.04 écrit « la demande simultanée de congé pour deux personnels n’est pas
          acceptée », soit <strong>1</strong>. Le laboratoire l’a porté à <strong>2</strong> le
          29/08/2026 — l’écart à la procédure est donc VOULU, et il se voit ici.
          <br />
          ⚠️ Zéro est refusé par le serveur : il interdirait tout congé.
        </p>

        <label style={lbl}>Délai de remise du certificat médical
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="number" min="1" max="720" style={{ ...inputStyle, width: 110 }}
              value={f.certificatDelaiHeures ?? 48}
              onChange={(e) => maj('certificatDelaiHeures', Number(e.target.value))} />
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>heures</span>
          </div>
        </label>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.55 }}>
          « Les personnels absents sont tenus de justifier leur absence pour cause de maladie par un
          certificat médical dans un délai ne dépassant pas les <strong>48 heures</strong> »
          (PRO.G1.04).
        </p>
      </Bloc>

      <Bloc titre="Décompte des jours">
        <label style={lbl}>Règle de décompte
          <select style={{ ...inputStyle, width: '100%' }} value={f.decompteMode || 'ouvrables'} onChange={(e) => maj('decompteMode', e.target.value)}>
            {Object.entries(MODES_DECOMPTE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginTop: 12, cursor: 'pointer' }}>
          <input type="checkbox" style={{ marginTop: 3, flex: 'none' }}
            checked={f.feriesDecomptes === true}
            onChange={(e) => maj('feriesDecomptes', e.target.checked)} />
          <span style={{ fontSize: 12.5, color: 'var(--text-strong)', lineHeight: 1.5 }}>
            Un jour férié chômé compris dans un congé se décompte du solde
          </span>
        </label>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.55 }}>
          {f.feriesDecomptes === true
            ? <>Un férié tombant à l’intérieur d’un congé <strong>consomme un jour</strong> de solde.
                C’est l’usage retenu par votre laboratoire ; il n’est pas universel, et il durcit le
                décompte pour le salarié. Le dimanche, lui, ne se décompte jamais.</>
            : <>Un férié tombant à l’intérieur d’un congé <strong>ne consomme pas</strong> de solde —
                sinon le salarié paierait deux fois le même jour. Le dimanche non plus.</>}
        </p>
      </Bloc>

      {/* ---- Mois de paie ---- */}
      <Bloc titre="Période de paie">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={lbl}>Mois de paie
            <select style={{ ...inputStyle, width: '100%' }} value={f.moisPaieMode || 'calendaire'} onChange={(e) => maj('moisPaieMode', e.target.value)}>
              <option value="calendaire">Calendaire (du 1er au dernier jour)</option>
              <option value="decale">Décalé</option>
            </select>
          </label>
          {f.moisPaieMode === 'decale' && (
            <label style={lbl}>Premier jour de la période
              <input type="number" min="1" max="28" style={{ ...inputStyle, width: '100%' }}
                value={f.moisPaieJourDebut ?? 1} onChange={(e) => maj('moisPaieJourDebut', Number(e.target.value))} />
            </label>
          )}
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8 }}>
          Un contrat peut porter son propre décalage — cette valeur est celle par défaut.
        </p>
      </Bloc>

      {/* ---- Ponctualité ---- */}
      <Bloc titre="Ponctualité">
        <label style={lbl}>Tolérance sur la prise de poste
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="number" min="0" max="60" style={{ ...inputStyle, width: 110 }}
              value={f.toleranceRetardMin ?? 5}
              onChange={(e) => maj('toleranceRetardMin', Number(e.target.value))} />
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>minutes</span>
          </div>
        </label>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.55 }}>
          Au-delà, l’arrivée est comptée comme un <strong>retard</strong> sur la journée réalisée.
          Le calcul se fait sur la <strong>première plage planifiée du jour</strong> — commencer à
          13 h ne fait pas cinq heures de retard — et sur la <strong>première entrée pointée</strong> :
          un aller-retour en cours de journée ne refait pas un retard.
          <br />
          Arriver en avance ne donne aucun crédit : la ponctualité se constate, elle ne se compense
          pas. Sans planning ou sans pointage, le retard n’est pas <em>mesurable</em> — il reste
          vide, jamais « à l’heure ».
          <br />
          <strong>0</strong> signifie « tout retard compte », et c’est un réglage valable.
        </p>
        {/* ⚠️ CES QUATRE RÉGLAGES ÉTAIENT ÉCRITS PAR SCRIPT, PAS PAR L'ÉCRAN
            (corrigé le 29/08/2026). Le serveur les acceptait et les validait
            depuis toujours ; l'écran ne les proposait pas. La RQ ne pouvait donc
            pas changer le seuil de retard opposable, le cumul valant absence, le
            délai du certificat ni le nombre de congés simultanés sans passer par
            moi — ce qui rend le paramétrage dépendant d'un tiers, et fait qu'un
            réglage change sans que personne au laboratoire l'ait décidé. */}
        <label style={lbl}>Seuil de retard <strong>opposable</strong>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="number" min="0" max="240" style={{ ...inputStyle, width: 110 }}
              value={f.seuilRetardOpposableMin ?? 30}
              onChange={(e) => maj('seuilRetardOpposableMin', Number(e.target.value))} />
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>minutes</span>
          </div>
        </label>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.55 }}>
          C’est le retard qui peut être <strong>reproché</strong>, à distinguer de la tolérance
          ci-dessus, qui n’est qu’un seuil de mesure. PRO.G1.04 en donne <strong>30 minutes</strong>.
          <br />
          ⚠️ Il ne peut pas être inférieur à la tolérance : on n’oppose pas un retard que le
          pointage ne compte même pas. Le serveur refuse l’enregistrement dans ce cas.
        </p>

        <label style={lbl}>Cumul de retards valant une absence
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="number" min="2" max="20" style={{ ...inputStyle, width: 110 }}
              value={f.retardsConsecutifsAbsence ?? 3}
              onChange={(e) => maj('retardsConsecutifsAbsence', Number(e.target.value))} />
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>retards consécutifs</span>
          </div>
        </label>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.55 }}>
          PRO.G1.04 : « un cumul de trois retards consécutifs est équivalent à une absence ».
        </p>

      </Bloc>

      {/* ---- Motifs ---- */}
      <Bloc titre="Motifs d’absence">
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 10 }}>
          Les durées maximales sont <strong>vides</strong> : à vous de les renseigner d’après votre
          convention. Le logiciel n’inscrit aucune durée légale de lui-même — un chiffre plausible
          mais faux serait pris pour argent comptant.
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table className="ged-tableau" ref={enTeteCollant} style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640 }}>
            <thead>
              <tr>
                {['Motif', 'Circuit', 'Décompté', 'Justificatif', 'Santé', 'Durée max (j)', 'Durée proposée (j)'].map((t) => (
                  <th scope="col" key={t} style={thP}>{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(f.motifsAbsence || []).map((m) => (
                <tr key={m.code}>
                  <td style={{ ...tdP, textAlign: 'start' }}>
                    <input style={{ ...inputStyle, width: '100%', fontSize: 12.5 }} value={m.label}
                      onChange={(e) => majMotif(m.code, 'label', e.target.value)} aria-label={`Libellé de ${m.code}`} />
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-faint)' }}>{m.code}</span>
                  </td>
                  <td style={tdP}>
                    <select style={{ ...inputStyle, fontSize: 12.5 }} value={m.circuit}
                      onChange={(e) => majMotif(m.code, 'circuit', e.target.value)} aria-label={`Circuit de ${m.code}`}>
                      <option value="demande">Demandé par le salarié</option>
                      <option value="saisie">Saisi par le RH</option>
                    </select>
                  </td>
                  <td style={tdP}><input type="checkbox" checked={m.decompteSolde} onChange={(e) => majMotif(m.code, 'decompteSolde', e.target.checked)} aria-label={`${m.code} décompté du solde`} /></td>
                  <td style={tdP}><input type="checkbox" checked={m.justificatifRequis} onChange={(e) => majMotif(m.code, 'justificatifRequis', e.target.checked)} aria-label={`${m.code} justificatif requis`} /></td>
                  <td style={tdP}>
                    <input type="checkbox" checked={m.sensible} onChange={(e) => majMotif(m.code, 'sensible', e.target.checked)} aria-label={`${m.code} donnée de santé`} />
                  </td>
                  <td style={tdP}>
                    <input type="number" min="0" style={{ ...inputStyle, width: 80, fontSize: 12.5 }} value={m.dureeMax ?? ''}
                      placeholder="—" onChange={(e) => majMotif(m.code, 'dureeMax', e.target.value === '' ? null : Number(e.target.value))}
                      aria-label={`Durée maximale de ${m.code}`} />
                  </td>
                  {/* ⚠️ PROPOSÉE, pas maximale. Celle d'à côté REFUSE au-delà ;
                      celle-ci ne fait que pré-remplir la date de fin, et le
                      congé se prolonge librement. Confondre les deux
                      interdirait la prolongation. */}
                  <td style={tdP}>
                    <input type="number" min="1" style={{ ...inputStyle, width: 80, fontSize: 12.5 }} value={m.dureeDefautJours ?? ''}
                      placeholder="—" onChange={(e) => majMotif(m.code, 'dureeDefautJours', e.target.value === '' ? null : Number(e.target.value))}
                      aria-label={`Durée proposée de ${m.code}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8 }}>
          « Santé » coché = le motif, le commentaire et le certificat ne sortent jamais du serveur
          pour un autre que vous et la personne concernée, et le journal d’audit n’en porte pas le motif.
          <br />
          <strong>Durée proposée</strong> ≠ <strong>durée max</strong> : la première pré-remplit
          seulement la date de fin à la saisie et se corrige librement ; la seconde <em>refuse</em>
          au-delà. La maternité est à 60 jours proposés — deux mois, l’usage du laboratoire — et
          sans maximum, puisqu’elle se prolonge.
        </p>
      </Bloc>

      {/* U2 (25/08/2026) — l'action était tout en bas, après trois écrans de
          réglages et un tableau : il fallait défiler pour savoir qu'elle
          existait. La barre reste collée au bas de la zone de défilement. */}
      <BarreActions aide={<>Ces règles décident de chiffres <strong>opposables à un salarié</strong> — congés, heures et majorations en découlent.</>}>
        <Bouton variante="primaire" disabled={envoi} onClick={async () => {
            setEnvoi(true);
            /* `vus` = les règles dont le formulaire est parti : envoyées en
               `base`, le serveur n'écrit que ce qui a changé (14/10/2026). */
            try { await saveRhParametres(f, vus); } finally { setEnvoi(false); }
          }}>
          {envoi ? 'Enregistrement…' : 'Enregistrer les règles'}
        </Bouton>
      </BarreActions>
    </section>
  );
}

/**
 * Jours fériés — onglet distinct du centre de paramétrage.
 *
 * Séparé des règles parce qu'il ne s'enregistre pas de la même façon : chaque
 * jour est écrit à l'unité, il n'y a pas de brouillon global à valider.
 */
export function JoursFeries() {
  const { rhAcces, rhFeries, saveFerie, supprimerFerie } = useGed();
  const [ferie, setFerie] = React.useState(false);
  /* ⚠️ DEUX VUES DU MÊME CALENDRIER, et elles ne répondent pas à la même
     question. Celle-ci SAISIT les dates — les fêtes religieuses sont lunaires
     et annoncées tardivement, il faut pouvoir les taper. L'autre les ORGANISE :
     régime payé ou non, équipe qui assure, jour de récupération, ce qui manque.
     Les fondre en une seule ferait un écran où l'on ne sait plus si l'on
     enregistre une date ou une organisation. */
  const [vue, setVue] = React.useState<'saisie' | 'annee'>('saisie');
  if (!rhAcces?.acces) return <AccesRefuseRh />;

  if (vue === 'annee') {
    return (
      <section>
        <Bouton taille="sm" onClick={() => setVue('saisie')} style={{ marginBottom: 12 }}>
          ← Saisie des dates
        </Bouton>
        <RhFeriesEcran />
      </section>
    );
  }

  return (
    <section>
      <Bouton taille="sm" onClick={() => setVue('annee')} style={{ marginBottom: 12 }}>
        Organisation de l’année — équipes, régimes, récupérations →
      </Bouton>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.55 }}>
        Calendrier saisi à la main, jamais calculé : les fêtes religieuses sont lunaires et
        annoncées tardivement — aucune formule ne peut les fixer d’avance. Un jour chômé n’est pas
        décompté des congés.
      </p>
      <Bouton taille="sm" onClick={() => setFerie(true)}><I n="plus" s={13} /> Ajouter un jour férié</Bouton>
      {!rhFeries.length ? (
        <p style={{ fontSize: 12.5, color: 'var(--text-faint)', marginTop: 12 }}>
          Aucun jour férié saisi : les congés seront décomptés comme des jours ordinaires.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {rhFeries.map((x) => (
            <div key={x.documentId} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12.5, borderBottom: '1px solid var(--border-subtle)', paddingBottom: 8 }}>
              <span style={{ fontFamily: 'var(--font-mono)', minWidth: 92 }}>{x.date}</span>
              <span style={{ flex: 1 }}>{x.libelle}</span>
              {!x.chome && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>non chômé</span>}
              <ConfirmButton label="" titre={`Retirer ${x.libelle}`} icon="trash" variant="danger"
                question={`Retirer « ${x.libelle} » du calendrier ?`} confirmer="Retirer"
                onConfirm={() => supprimerFerie(x.documentId)} />
            </div>
          ))}
        </div>
      )}
      {/* ⚠️ LA FENÊTRE NE SE FERME PLUS À L'ENREGISTREMENT (RQ, 26/08/2026) :
          « quand j'enregistre quelque chose dans la fenêtre de paramétrage, je
          ne veux pas quitter la page popup ». On ajoute rarement UN jour férié :
          on en ajoute neuf d'affilée, et rouvrir la fenêtre à chaque fois — en
          reperdant sa place dans la liste — était le vrai coût. */}
      {ferie && (
        <FormulaireFerie onFermer={() => setFerie(false)} onValider={saveFerie} />
      )}
    </section>
  );
}

/**
 * Régimes spéciaux — les périodes où le laboratoire ne tourne pas à son rythme
 * ordinaire.
 *
 * Onglet distinct des jours fériés parce que ce n'est pas la même chose : un
 * férié est un jour, un régime est une plage, et surtout un férié n'arrête pas
 * l'activité ici — le laboratoire travaille les fériés. Le régime, lui, ferme.
 *
 * `postesMaintenus` est ce qui rend la période utilisable : pendant le régime
 * Ramadan de 2026, l'entretien des locaux a continué tous les jours. Une
 * période qui fermerait tout indistinctement serait fausse dès son premier
 * emploi.
 */
export function RegimesSpeciaux() {
  const { rhAcces, rhRegimes, rhPostes, saveRegime, supprimerRegime } = useGed();
  const [ouvert, setOuvert] = React.useState(false);
  if (!rhAcces?.acces) return <AccesRefuseRh />;

  return (
    <section>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.55 }}>
        Une plage de dates pendant laquelle les postes ferment — Ramadan, fermeture annuelle,
        travaux. Déclarée une fois pour tout le laboratoire : l’écrire poste par poste
        demanderait deux cents exceptions à ressaisir chaque année, et la moindre oubliée
        passerait pour un poste que personne n’a tenu.
      </p>
      <Bouton taille="sm" onClick={() => setOuvert(true)}><I n="plus" s={13} /> Déclarer une période</Bouton>
      {!rhRegimes.length ? (
        <p style={{ fontSize: 12.5, color: 'var(--text-faint)', marginTop: 12 }}>
          Aucune période déclarée : tous les postes suivent leur calendrier toute l’année.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {rhRegimes.map((x) => (
            <div key={x.documentId} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12.5, borderBottom: '1px solid var(--border-subtle)', paddingBottom: 8 }}>
              <span style={{ fontFamily: 'var(--font-mono)', minWidth: 172 }}>{x.dateDebut} → {x.dateFin}</span>
              <span style={{ flex: 1 }}>
                {x.libelle}
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)' }}>
                  {x.postesMaintenus?.length
                    ? `reste ouvert : ${x.postesMaintenus.join(', ')}`
                    : 'tous les postes ferment'}
                </span>
                {/* Les horaires réarrangés se LISENT dans la liste : une
                    période qui déplace des heures sans le montrer ferait
                    chercher l'explication sur chaque poste, un par un. */}
                {x.horairesParPoste && Object.keys(x.horairesParPoste).length > 0 && (
                  <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)' }}>
                    horaires du régime :{' '}
                    {Object.entries(x.horairesParPoste)
                      .map(([c, h]) => `${c} ${h.debut || '—'}→${h.fin || '—'}`)
                      .join(' · ')}
                  </span>
                )}
              </span>
              <ConfirmButton label="" titre={`Retirer ${x.libelle}`} icon="trash" variant="danger"
                question={`Retirer la période « ${x.libelle} » ? Les postes concernés redeviendront ouverts sur ces dates.`}
                confirmer="Retirer" onConfirm={() => supprimerRegime(x.documentId)} />
            </div>
          ))}
        </div>
      )}
      {ouvert && (
        <FormulaireRegime postes={rhPostes} onFermer={() => setOuvert(false)}
          onValider={saveRegime} />
      )}
    </section>
  );
}

function FormulaireRegime({ postes, onFermer, onValider }: {
  postes: { code: string; libelle: string }[];
  onFermer: () => void;
  onValider: (d: { code: string; libelle: string; dateDebut: string; dateFin: string;
    postesMaintenus: string[]; horairesParPoste: Record<string, { debut: string; fin: string }>; note: string }) => Promise<boolean>;
}) {
  const [code, setCode] = React.useState('');
  const [libelle, setLibelle] = React.useState('');
  const [dateDebut, setDebut] = React.useState('');
  const [dateFin, setFin] = React.useState('');
  /* Ce qui vient d'être déclaré. La fenêtre reste ouverte (RQ, 26/08/2026) :
     sans ce signe, on ne sait plus si le clic a porté — et l'on redéclare. */
  const [declares, setDeclares] = React.useState<string[]>([]);
  const [maintenus, setMaintenus] = React.useState<string[]>([]);
  /* LES HORAIRES DU RÉGIME, poste par poste (A19). Vides = le poste garde les
     siens — c'est le cas de l'entretien des locaux pendant le Ramadan 2026. */
  const [horaires, setHoraires] = React.useState<Record<string, { debut: string; fin: string }>>({});
  const majHoraire = (code: string, quoi: 'debut' | 'fin', v: string) => setHoraires((h) => {
    const actuel = h[code] || { debut: '', fin: '' };
    return { ...h, [code]: { ...actuel, [quoi]: v } };
  });
  const [note, setNote] = React.useState('');
  const [envoi, setEnvoi] = React.useState(false);
  useEscape(!envoi, onFermer);
  const pret = !!code.trim() && !!libelle.trim() && !!dateDebut && !!dateFin;

  return (
    <Modale titre="Déclarer une période de régime spécial" onFermer={onFermer}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
        <label style={lblRh}>Code
          <input style={{ ...inputStyle, width: '100%' }} value={code} onChange={(e) => setCode(e.target.value)} placeholder="RAMADAN-2026" />
        </label>
        <label style={lblRh}>Intitulé
          <input style={{ ...inputStyle, width: '100%' }} value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder="Régime Ramadan" />
        </label>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
        <label style={lblRh}>Du
          <input type="date" style={{ ...inputStyle, width: '100%' }} value={dateDebut} onChange={(e) => setDebut(e.target.value)} />
        </label>
        <label style={lblRh}>Au (inclus)
          <input type="date" style={{ ...inputStyle, width: '100%' }} value={dateFin} onChange={(e) => setFin(e.target.value)} />
        </label>
      </div>

      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>Postes qui restent ouverts</div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 6px', lineHeight: 1.5 }}>
          Ne rien cocher = tous les postes ferment. En 2026, l’entretien des locaux avait continué
          pendant toute la quinzaine.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {postes.map((p) => {
            const on = maintenus.includes(p.code);
            return (
              <button key={p.code} type="button" aria-pressed={on}
                onClick={() => setMaintenus((m) => (on ? m.filter((c) => c !== p.code) : [...m, p.code]))}
                style={{
                  fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                  borderRadius: 'var(--radius-pill)', padding: '5px 11px',
                  border: `1px solid ${on ? 'var(--color-primary-active)' : 'var(--border-default)'}`,
                  background: on ? 'var(--cyan-50)' : 'var(--surface-card)',
                  color: on ? 'var(--color-primary-active)' : 'var(--text-muted)',
                }}>
                {p.libelle}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── LES HORAIRES DU RÉGIME ─────────────────────────────────────────
          Ils n'apparaissent que pour les postes MAINTENUS : un poste fermé n'a
          pas d'horaires, et proposer des champs pour lui ferait saisir une
          donnée que le serveur refuse ensuite — on aurait travaillé pour rien
          et l'on ne comprendrait pas le refus. */}
      {maintenus.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>Horaires pendant la période</div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 6px', lineHeight: 1.5 }}>
            Laisser vide = le poste garde ses horaires ordinaires. Ils s’appliquent à la lecture :
            la période finie, les horaires d’origine reviennent d’eux-mêmes, sans rien à remettre à la main.
          </p>
          <div style={{ display: 'grid', gap: 6 }}>
            {maintenus.map((code) => {
              const p = postes.find((x) => x.code === code);
              return (
                <div key={code} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                  <span style={{ flex: 1, minWidth: 0 }}>{p?.libelle || code}</span>
                  <input type="time" aria-label={`Début pendant la période — ${p?.libelle || code}`}
                    style={{ ...inputStyle, width: 110 }}
                    value={horaires[code]?.debut || ''} onChange={(e) => majHoraire(code, 'debut', e.target.value)} />
                  <span style={{ color: 'var(--text-muted)' }}>→</span>
                  <input type="time" aria-label={`Fin pendant la période — ${p?.libelle || code}`}
                    style={{ ...inputStyle, width: 110 }}
                    value={horaires[code]?.fin || ''} onChange={(e) => majHoraire(code, 'fin', e.target.value)} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <label style={{ ...lblRh, marginTop: 12 }}>Note
        <input style={{ ...inputStyle, width: '100%' }} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Régime individuel : chacun a ses horaires propres" />
      </label>

      {!!declares.length && (
        <div role="status" style={{ marginTop: 12, fontSize: 12.5, color: 'var(--green-700)', lineHeight: 1.5 }}>
          Déclaré : <strong>{declares.join(', ')}</strong>. Les postes maintenus et leurs horaires sont
          gardés — d’un régime au suivant, ce sont souvent les mêmes.
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'end', marginTop: 16 }}>
        {/* « Annuler » n'annulerait rien une fois quelque chose enregistré. */}
        <Bouton taille="sm" onClick={onFermer} disabled={envoi}>
          {declares.length ? 'Fermer' : 'Annuler'}
        </Bouton>
        <Bouton variante="primaire" taille="sm" disabled={!pret || envoi} onClick={async () => {
            setEnvoi(true);
            try {
              /* On n'envoie QUE les postes maintenus, et QUE ceux dont au moins
                 une heure est saisie : une clé vide serait refusée par le
                 serveur, et une clé sur un poste décoché entre-temps aussi. */
              const horairesRetenus: Record<string, { debut: string; fin: string }> = {};
              for (const c of maintenus) {
                const h = horaires[c];
                if (h && (h.debut || h.fin)) horairesRetenus[c] = { debut: h.debut, fin: h.fin };
              }
              const ok = await onValider({
                code: code.trim(), libelle: libelle.trim(), dateDebut, dateFin,
                postesMaintenus: maintenus, horairesParPoste: horairesRetenus, note,
              });
              /* Seule l'IDENTITÉ du régime est effacée — code, libellé, dates.
                 Les postes maintenus et leurs horaires restent : d'un régime au
                 suivant ce sont les mêmes, et les ressaisir vingt fois est
                 exactement le travail qu'on cherche à éviter.

                 ⚠️ Le code DOIT partir : le garder ferait déclarer deux fois le
                 même régime au second clic. */
              if (ok) {
                setDeclares((l) => [...l, libelle.trim() || code.trim()]);
                setCode(''); setLibelle(''); setDebut(''); setFin(''); setNote('');
              }
            }
            finally { setEnvoi(false); }
          }}>
          {envoi ? 'Enregistrement…' : 'Déclarer'}
        </Bouton>
      </div>
    </Modale>
  );
}

const lblRh: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
  fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-body)',
};

function BaremeAnciennete({ bareme, onChange }: {
  bareme: { ansMin: number; jours: number }[];
  onChange: (b: { ansMin: number; jours: number }[]) => void;
}) {
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Tranches d’ancienneté</div>
      {bareme.map((t, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 12.5 }}>à partir de</span>
          <input type="number" min="0" style={{ ...inputStyle, width: 76 }} value={t.ansMin}
            onChange={(e) => onChange(bareme.map((x, j) => (j === i ? { ...x, ansMin: Number(e.target.value) } : x)))}
            aria-label={`Ancienneté minimale, tranche ${i + 1}`} />
          <span style={{ fontSize: 12.5 }}>an(s) →</span>
          <input type="number" min="0" step="0.5" style={{ ...inputStyle, width: 76 }} value={t.jours}
            onChange={(e) => onChange(bareme.map((x, j) => (j === i ? { ...x, jours: Number(e.target.value) } : x)))}
            aria-label={`Jours de congé, tranche ${i + 1}`} />
          <span style={{ fontSize: 12.5 }}>jours</span>
          <Bouton variante="danger" taille="sm" onClick={() => onChange(bareme.filter((_, j) => j !== i))} aria-label={`Retirer la tranche ${i + 1}`}>✕</Bouton>
        </div>
      ))}
      <Bouton taille="sm" onClick={() => onChange([...bareme, { ansMin: 0, jours: 0 }])}>
        <I n="plus" s={13} /> Ajouter une tranche
      </Bouton>
    </div>
  );
}

function FormulaireFerie({ onFermer, onValider }: {
  onFermer: () => void; onValider: (d: { date: string; libelle: string; chome: boolean }) => Promise<boolean>;
}) {
  const [date, setDate] = React.useState('');
  const [libelle, setLibelle] = React.useState('');
  const [chome, setChome] = React.useState(true);
  const [envoi, setEnvoi] = React.useState(false);
  /* Ce qui vient d'être ajouté, NOMMÉ. La fenêtre restant ouverte, il faut un
     signe que le clic a porté — sans lui on ne sait plus si l'on a enregistré,
     et l'on réenregistre. Le nom plutôt qu'un « c'est fait » : il dit AUSSI
     lequel, quand on en enchaîne neuf. */
  const [ajoutes, setAjoutes] = React.useState<string[]>([]);
  useEscape(!envoi, onFermer);
  return (
    <Modale titre="Ajouter un jour férié" onFermer={onFermer}>
      <label style={lbl}>Date
        <input type="date" style={{ ...inputStyle, width: '100%' }} value={date} onChange={(e) => setDate(e.target.value)} />
      </label>
      <label style={{ ...lbl, marginTop: 10 }}>Intitulé
        <input style={{ ...inputStyle, width: '100%' }} value={libelle} onChange={(e) => setLibelle(e.target.value)}
          placeholder="Ex. : Fête de la Révolution" />
      </label>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, marginTop: 10 }}>
        <input type="checkbox" checked={chome} onChange={(e) => setChome(e.target.checked)} />
        Jour chômé (non décompté des congés)
      </label>
      {!!ajoutes.length && (
        <div role="status" style={{ marginTop: 12, fontSize: 12.5, color: 'var(--green-700)', lineHeight: 1.5 }}>
          Ajouté{ajoutes.length > 1 ? 's' : ''} : <strong>{ajoutes.join(', ')}</strong>. Vous pouvez en saisir un autre.
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'end', marginTop: 16 }}>
        {/* « Annuler » deviendrait un mensonge une fois quelque chose enregistré
            — il n'annulerait rien. Le bouton dit alors ce qu'il fait vraiment. */}
        <Bouton taille="sm" onClick={onFermer} disabled={envoi}>
          {ajoutes.length ? 'Fermer' : 'Annuler'}
        </Bouton>
        <Bouton variante="primaire" disabled={!date || !libelle || envoi} onClick={async () => {
            setEnvoi(true);
            try {
              const ok = await onValider({ date, libelle, chome });
              /* Les champs se vident pour la saisie suivante — mais SEULEMENT
                 si l'enregistrement a réussi. Les effacer sur un échec ferait
                 perdre ce qu'on venait de taper. */
              if (ok) { setAjoutes((l) => [...l, libelle]); setDate(''); setLibelle(''); }
            } finally { setEnvoi(false); }
          }}>
          {envoi ? 'Ajout…' : 'Ajouter'}
        </Bouton>
      </div>
    </Modale>
  );
}

function Bloc({ titre, children }: { titre: string; children: React.ReactNode }) {
  return (
    <div style={{ ...card, padding: 20, marginTop: 14 }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, margin: '0 0 10px' }}>{titre}</h2>
      {children}
    </div>
  );
}

const thP: React.CSSProperties = {
  fontFamily: 'var(--font-sans)', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '.03em', color: 'var(--text-muted)', padding: '7px 8px',
  borderBottom: '1px solid var(--border-default)', textAlign: 'center',
};
const tdP: React.CSSProperties = {
  padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', textAlign: 'center', verticalAlign: 'top',
};
